import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ArchiveIcon from '@mui/icons-material/Archive';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NoteIcon from '@mui/icons-material/Note';
import PersonIcon from '@mui/icons-material/Person';
import { SeverityChip } from 'components/shared/SeverityChip';
import { getTimeAgo } from 'components/shared/utils';
import IncidentStatusBadge from '../shared/IncidentStatusBadge';
import { getActionsForStatus } from './incidentActionsByStatus';
import type {
  Incident,
  IncidentDetail,
  IncidentStatus,
  IncidentTimelineEntry,
} from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IncidentAccordionRowProps {
  /** The incident to display. May be a basic Incident or a full IncidentDetail. */
  incident: Incident | IncidentDetail;
  /** Called when the user clicks "View" */
  onView?: (incident: Incident) => void;
  /** Called when the user clicks a status-changing action button */
  onStatusChange?: (incident: Incident, status: IncidentStatus) => void;
  /** Called for Quick-Resolve (False Alarm) — supplies the reason string */
  onQuickResolve?: (incident: Incident, reason: string) => void;
}

// ---------------------------------------------------------------------------
// Icon mapping — maps the string keys in incidentActionsByStatus to elements
// ---------------------------------------------------------------------------

const ICON_MAP: Record<string, React.ReactElement> = {
  Visibility: <VisibilityIcon fontSize="small" />,
  PlayArrow: <PlayArrowIcon fontSize="small" />,
  CheckCircle: <CheckCircleIcon fontSize="small" />,
  LocalShipping: <LocalShippingIcon fontSize="small" />,
  Send: <SendIcon fontSize="small" />,
  Close: <CloseIcon fontSize="small" />,
  Archive: <ArchiveIcon fontSize="small" />,
};

// ---------------------------------------------------------------------------
// Mini-timeline helpers (mirrors IncidentTimeline.tsx, simplified)
// ---------------------------------------------------------------------------

function getTimelineIcon(
  action: IncidentTimelineEntry['action'],
  toStatus?: IncidentStatus | null,
): React.ReactElement {
  if (action === 'note_added') return <NoteIcon fontSize="small" />;
  if (action === 'assigned') return <PersonIcon fontSize="small" />;
  if (action === 'dispatched') return <LocalShippingIcon fontSize="small" color="secondary" />;

  switch (toStatus) {
    case 'reviewing':
      return <PlayArrowIcon fontSize="small" color="warning" />;
    case 'dispatched':
      return <LocalShippingIcon fontSize="small" color="secondary" />;
    case 'resolved':
      return <CheckCircleIcon fontSize="small" color="success" />;
    case 'closed':
      return <ArchiveIcon fontSize="small" color="action" />;
    case 'dismissed':
      return <CloseIcon fontSize="small" color="action" />;
    default:
      return <FiberManualRecordIcon fontSize="small" color="primary" />;
  }
}

function getTimelineDescription(entry: IncidentTimelineEntry): string {
  const user = entry.user_name || 'System';
  switch (entry.action) {
    case 'created':
      return `Incident created${entry.details ? ` (${entry.details})` : ''}`;
    case 'status_changed':
      return `${user} changed status to ${entry.to_status?.toUpperCase()}`;
    case 'note_added':
      return `${user} added a note`;
    case 'dispatched':
      return `${user} dispatched guard`;
    case 'assigned':
      return `${user} assigned to incident`;
    default:
      return entry.details || 'Unknown action';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function IncidentAccordionRow({
  incident,
  onView,
  onStatusChange,
  onQuickResolve,
}: IncidentAccordionRowProps) {
  const cameraName =
    incident.camera_display_name || incident.camera_name || 'Unknown Camera';
  const siteName =
    incident.site_display_name || incident.site_name || 'Unknown Site';

  // Timeline entries — only present on IncidentDetail
  const timeline: IncidentTimelineEntry[] =
    (incident as IncidentDetail).timeline ?? [];
  const recentTimeline = [...timeline]
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
    .slice(0, 3);

  // Actions for the current status
  const actions = getActionsForStatus(incident.status);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleActionClick = (action: IncidentStatus | null, _label: string) => {
    // "View" action (action === null)
    if (action === null) {
      onView?.(incident);
      return;
    }

    // Standard status change
    onStatusChange?.(incident, action);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
      {/* ----------------------------------------------------------------- */}
      {/* LEFT: Details (75% on sm+, 100% on xs) */}
      {/* ----------------------------------------------------------------- */}
      <Paper
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', flex: { xs: 'none', sm: 3 }, width: { xs: '100%', sm: 'auto' }, p: 2 }}
      >
        {/* Title — full width */}
        <Typography variant="subtitle1" fontWeight="bold">
          {incident.title}
        </Typography>

        {/* Description — full width */}
        {incident.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {incident.description}
          </Typography>
        )}

        {/* 2-column detail grid — stacks to 1 column on xs */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mt: 2,
          }}
        >
          {/* Type */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Type
            </Typography>
            <Typography variant="body2">{incident.incident_type}</Typography>
          </Box>

          {/* Camera */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Camera
            </Typography>
            <Typography variant="body2">{cameraName}</Typography>
          </Box>

          {/* Site */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Site
            </Typography>
            <Typography variant="body2">{siteName}</Typography>
          </Box>

          {/* Severity */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Severity
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <SeverityChip severity={incident.severity} size="small" />
            </Box>
          </Box>

          {/* Status */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <IncidentStatusBadge status={incident.status} size="small" />
            </Box>
          </Box>

          {/* Created */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">
              {getTimeAgo(incident.created_at)}
            </Typography>
          </Box>
        </Box>

        {/* Mini timeline — last 3 entries */}
        {recentTimeline.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="medium"
              sx={{ mb: 0.5, display: 'block' }}
            >
              Recent Activity
            </Typography>
            <List dense disablePadding>
              {recentTimeline.map((entry) => (
                <ListItem key={entry.id} sx={{ py: 0.25, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {getTimelineIcon(entry.action, entry.to_status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={getTimelineDescription(entry)}
                    secondary={getTimeAgo(entry.created_at)}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* ----------------------------------------------------------------- */}
      {/* RIGHT: Actions (25% on sm+, 100% on xs) */}
      {/* ----------------------------------------------------------------- */}
      <Paper
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', flex: { xs: 'none', sm: 1 }, width: { xs: '100%', sm: 'auto' }, p: 2 }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight="medium"
          sx={{ mb: 1, display: 'block' }}
        >
          Actions
        </Typography>
        <Stack spacing={1}>
          {actions.map((actionDef) => (
            <Button
              key={actionDef.label}
              variant={actionDef.variant}
              color={actionDef.color}
              size="small"
              fullWidth
              startIcon={ICON_MAP[actionDef.icon] ?? null}
              onClick={() =>
                handleActionClick(actionDef.action, actionDef.label)
              }
            >
              {actionDef.label}
            </Button>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}

export default IncidentAccordionRow;
